'use client';

import { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import { Send, Square, RefreshCw, ChevronDown, Home, X, Copy, Check, Paperclip } from 'lucide-react';
import { MarkdownBlock } from './Markdown';
import { ToolBlock } from './ToolBlock';
import { CommandPalette, getFilteredCommands } from './CommandPalette';
import { useChat } from './useChat';
import { StatusBar } from '../layout/StatusBar';
import { useActiveProject, type ActiveProject } from '@/lib/useActiveProject';
import { cn } from '@/lib/utils';
import { gateway, soma, sessionApi, projectsApi } from '@/lib/api';
import { ScratchpadPanel } from '../soma/ScratchpadPanel';
import { SelectionReadMenu } from './SelectionReadMenu';
import type { ChatMessage, ToolCall, MessagePart } from './types';

// Synchronous check for whether localStorage has a saved chat for this slug.
// Used to suppress the EmptyState flash during the (potentially MB-sized)
// async hydration window — if data exists on disk, we're about to render it
// and showing "no messages" first reads as the bug "history disappeared".
function hasSavedChat(slug: string | null | undefined): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const key = slug ? `beast:chat:messages:${slug}` : 'beast:chat:messages:general';
    const stored = localStorage.getItem(key);
    return stored !== null && stored !== '[]';
  } catch {
    return false;
  }
}

// --- Message Components ---

// Extract the plain-text content of a message for copying (handles parts + content).
function messageText(msg: ChatMessage): string {
  if (msg.parts && msg.parts.length > 0) {
    return msg.parts
      .filter(p => p.type === 'text')
      .map(p => (p as { content: string }).content)
      .join('\n\n')
      .trim();
  }
  return (msg.content || '').trim();
}

// Hover copy button — copies the given text to the clipboard, shows a check for 1.2s.
function CopyButton({ text, align = 'left' }: { text: string; align?: 'left' | 'right' }) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  }, [text]);
  return (
    <button
      onClick={copy}
      title={copied ? 'Copied' : 'Copy text'}
      className="copy-btn"
      style={{
        position: 'absolute', top: 4, [align === 'right' ? 'left' : 'right']: 4,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 22, height: 22, borderRadius: 5, border: 'none', cursor: 'pointer',
        background: 'rgba(255,255,255,0.06)', color: copied ? '#4ade80' : '#8b95a5',
        opacity: 0, transition: 'opacity .12s',
      }}
    >
      {copied ? <Check style={{ width: 13, height: 13 }} /> : <Copy style={{ width: 13, height: 13 }} />}
    </button>
  );
}

// Thought process — collapsed by default in history, open while it streams live.
// Dim + italic so it can never be mistaken for the actual reply (2026-09-04 scar).
function ThinkingBlock({ content, defaultOpen = false }: { content: string; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!content.trim()) return null;
  return (
    <div className="my-1">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5"
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', color: '#6b7280' }}
      >
        <ChevronDown style={{ width: 12, height: 12, transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform .12s' }} />
        <span style={{ fontSize: '11px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>✦ Thought process</span>
      </button>
      {open && (
        <div style={{
          fontSize: '12.5px', color: '#8b95a5', fontStyle: 'italic', whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere', borderLeft: '2px solid #2f3542', paddingLeft: 10, margin: '4px 0 4px 5px',
        }}>{content}</div>
      )}
    </div>
  );
}

function DeleteButton({ align = 'left', onDelete }: { align?: 'left' | 'right'; onDelete: () => void }) {
  const del = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  }, [onDelete]);
  return (
    <button
      onClick={del}
      title="Delete message — forgets it from memory"
      className="delete-btn"
      style={{
        position: 'absolute', top: 4, [align === 'right' ? 'left' : 'right']: 30,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 22, height: 22, borderRadius: 5, border: 'none', cursor: 'pointer',
        background: 'rgba(255,255,255,0.06)', color: '#8b95a5',
        opacity: 0, transition: 'opacity .12s',
      }}
    >
      <X style={{ width: 13, height: 13 }} />
    </button>
  );
}

const MessageBubble = memo(function MessageBubble({ msg, onDelete }: { msg: ChatMessage; onDelete?: (id: string) => void }) {
  if (msg.isKeepAlive) {
    return (
      <div className="flex justify-center py-1">
        <span className="text-[10px] text-pink-400/40 font-mono">{msg.content}</span>
      </div>
    );
  }
  if (msg.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[75%]">
          {msg.images && msg.images.length > 0 && (
            <div className="flex gap-2 mb-2 justify-end flex-wrap">
              {msg.images.map((img, i) => (
                <img
                  key={i}
                  src={`data:${img.media_type};base64,${img.data}`}
                  alt={img.name || `Image ${i + 1}`}
                  className="max-w-[200px] max-h-[200px] rounded-lg object-cover"
                  style={{ border: '1px solid #2a3545' }}
                />
              ))}
            </div>
          )}
          <div className="msg-bubble rounded-lg px-4 py-3" style={{ backgroundColor: '#2a3545', position: 'relative' }}>
            <CopyButton text={msg.content || ''} align="right" />
            {onDelete && <DeleteButton align="right" onDelete={() => onDelete(msg.id)} />}
            {/* overflowWrap anywhere — long unbroken paths must wrap, not force a horizontal scroll (Boss 2026-07-10) */}
            <p style={{ fontSize: '14px', color: '#d4d4d4', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{msg.content}</p>
          </div>
          <div style={{ fontSize: '10px', color: '#5c6370', textAlign: 'right', marginTop: '4px' }}>
            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-start">
      <div className="msg-bubble max-w-[90%] min-w-0 rounded-lg px-4 py-3" style={{ backgroundColor: '#232530', position: 'relative' }}>
        <CopyButton text={messageText(msg)} align="left" />
        {onDelete && <DeleteButton align="left" onDelete={() => onDelete(msg.id)} />}
        <div className="flex items-center gap-1.5 mb-2">
          <span style={{ fontSize: '12px' }}>🦾</span>
          <span style={{ fontSize: '11px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#5c6370' }}>Beast</span>
          <span style={{ fontSize: '10px', color: '#3a3a4a' }}>
            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        {msg.parts ? (
          msg.parts.map((part, i) =>
            part.type === 'text' ? (
              <div key={`text-${i}`} className="my-1">
                <MarkdownBlock text={part.content} />
              </div>
            ) : part.type === 'thinking' ? (
              <ThinkingBlock key={`think-${i}`} content={part.content} />
            ) : (
              <ToolBlock key={`${part.tool.id}-${i}`} name={part.tool.name} input={part.tool.input} output={part.tool.output} status={part.tool.status} />
            )
          )
        ) : (
          <>
            {msg.tools?.map(tool => (
              <ToolBlock key={tool.id} name={tool.name} input={tool.input} output={tool.output} status={tool.status} />
            ))}
            {msg.content && (
              <div className="my-1">
                <MarkdownBlock text={msg.content} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});

function StreamingBubble({ text, tools, parts, thinking }: { text: string; tools: ToolCall[]; parts: MessagePart[]; thinking: boolean }) {
  const hasRunningTool = tools.some(t => t.status === 'running');
  const hasContent = text || parts.some(p => p.type !== 'thinking');

  return (
    <div className="flex justify-start">
      <div className="max-w-[90%] min-w-0 rounded-lg px-4 py-3" style={{ backgroundColor: '#232530' }}>
        <div className="flex items-center gap-1.5 mb-2">
          <span style={{ fontSize: '12px' }}>🦾</span>
          <span style={{ fontSize: '11px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#5c6370' }}>Beast</span>
        </div>
        {/* Render committed parts (text before tools, tools, text between tools) */}
        {parts.map((part, i) =>
          part.type === 'text' ? (
            <div key={`part-${i}`} className="my-1">
              <MarkdownBlock text={part.content} />
            </div>
          ) : part.type === 'thinking' ? (
            /* live thought — the last thinking part streams open until real text arrives */
            <ThinkingBlock key={`think-${i}`} content={part.content} defaultOpen={i === parts.length - 1 && !text} />
          ) : (
            <ToolBlock key={`${part.tool.id}-${i}`} name={part.tool.name} input={part.tool.input} output={part.tool.output} status={part.tool.status} />
          )
        )}
        {/* Currently streaming text (after the last tool) */}
        {text && (
          <div className="my-1">
            <MarkdownBlock text={text} />
            <span className="inline-block w-1.5 h-4 bg-cyan-400 animate-pulse rounded-sm ml-0.5 align-text-bottom" />
          </div>
        )}
        {/* Thinking indicator — no content yet */}
        {thinking && !hasContent && !hasRunningTool && (
          <div className="flex items-center gap-2 py-2">
            <div className="w-3 h-3 border border-gray-600 border-t-gray-400 rounded-full animate-spin" />
            <span className="text-[11px] text-gray-600">Thinking…</span>
          </div>
        )}
        {/* Activity indicators */}
        {hasRunningTool && (
          <div className="flex items-center gap-2 mt-1 py-1">
            <div className="w-3 h-3 border border-gray-600 border-t-gray-400 rounded-full animate-spin" />
            <span className="text-[11px] text-gray-600">Running…</span>
          </div>
        )}
        {/* Writing indicator — tools done, text generation in progress but nothing streamed yet */}
        {!text && !thinking && !hasRunningTool && parts.length > 0 && (
          <div className="flex items-center gap-2 mt-1 py-1">
            <span className="inline-block w-1.5 h-4 bg-cyan-400/60 animate-pulse rounded-sm" />
            <span className="text-[11px] text-cyan-500/70">Writing response…</span>
          </div>
        )}
      </div>
    </div>
  );
}

// --- Project Dropdown ---

type ProjectOption = {
  slug: string;
  name: string;
  icon: string | null;
  status: string;
  goal: string | null;
  path: string;
};

function ProjectSelector({ activeProject, setActiveProject }: {
  activeProject: ActiveProject | null;
  setActiveProject: (p: ActiveProject | null) => void;
}) {
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    projectsApi.list()
      .then((data: any) => {
        if (data.projects) {
          setProjects(data.projects.filter((p: ProjectOption) => p.status !== 'archived'));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors border',
          activeProject
            ? 'bg-purple-500/10 border-purple-500/20 text-purple-300'
            : 'bg-[#0d1117]/85/60 border-[rgb(var(--border))]/60 text-gray-400 hover:text-gray-200 hover:border-gray-700'
        )}
      >
        {activeProject ? (
          <span className="text-sm">{activeProject.icon || '📁'}</span>
        ) : (
          <Home className="w-3.5 h-3.5" />
        )}
        <span className="max-w-[200px] truncate">
          {activeProject ? activeProject.name : 'Beast'}
        </span>
        {activeProject && (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); setActiveProject(null); setOpen(false); }}
            className="p-0.5 rounded hover:bg-white/10 text-gray-500 hover:text-gray-300 transition-colors cursor-pointer"
          >
            <X className="w-3 h-3" />
          </span>
        )}
        <ChevronDown className={cn('w-3 h-3 text-gray-500 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 w-72 bg-gray-900 border border-[rgb(var(--border))]/60 rounded-lg shadow-xl z-50 max-h-[60vh] overflow-y-auto">
          <button
            onClick={() => { setActiveProject(null); setOpen(false); }}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[rgb(var(--muted))]/80 transition-colors',
              !activeProject ? 'bg-cyan-500/10 border-l-2 border-cyan-400' : 'border-l-2 border-transparent'
            )}
          >
            <Home className="w-4 h-4 text-gray-500" />
            <div>
              <div className="text-sm font-medium text-gray-200">Beast</div>
              <div className="text-[11px] text-gray-500">Main chat — voice conversations & delegated tasks land here</div>
            </div>
          </button>
          <div className="border-t border-white/[0.04] my-1" />
          {projects.map(p => (
            <button
              key={p.slug}
              onClick={() => {
                setActiveProject({ slug: p.slug, name: p.name, goal: p.goal, path: p.path, icon: p.icon });
                setOpen(false);
              }}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[rgb(var(--muted))]/80 transition-colors',
                activeProject?.slug === p.slug ? 'bg-purple-500/10 border-l-2 border-purple-400' : 'border-l-2 border-transparent'
              )}
            >
              <span className="text-base flex-shrink-0">{p.icon || '📁'}</span>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-gray-200 truncate">{p.name}</div>
                <div className="text-[10px] text-gray-500">{p.status}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Empty State (project context card) ---

function EmptyState({ activeProject }: { activeProject: ActiveProject | null }) {
  const [projectData, setProjectData] = useState<{
    name: string; goal: string | null; description: string | null;
    milestones: { name: string; status: string; tasks: { title: string; status: string }[] }[];
  } | null>(null);

  useEffect(() => {
    if (!activeProject) { setProjectData(null); return; }
    projectsApi.get(activeProject.slug)
      .then((data: any) => { if (data.name) setProjectData(data); })
      .catch(() => {});
  }, [activeProject]);

  if (!activeProject) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-600 gap-3">
        <span className="text-4xl">🦾</span>
        <div className="text-sm">Beast is ready</div>
        <div className="text-xs text-gray-700">/clear to reset · /new to start fresh</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full gap-4 px-6">
      <span className="text-4xl">{activeProject.icon || '📁'}</span>
      <div className="text-lg font-semibold text-white">{activeProject.name}</div>
      {activeProject.goal && (
        <p className="text-sm text-gray-400 text-center max-w-md">{activeProject.goal}</p>
      )}
      {projectData && projectData.milestones?.length > 0 && (
        <div className="text-xs text-gray-500 space-y-1 max-w-sm">
          {projectData.milestones.slice(0, 5).map((m, i) => {
            const done = m.tasks?.filter(t => t.status === 'complete').length || 0;
            const total = m.tasks?.length || 0;
            return (
              <div key={i} className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${m.status === 'complete' ? 'bg-emerald-400' : m.status === 'in_progress' ? 'bg-cyan-400' : 'bg-gray-600'}`} />
                <span className="flex-1 truncate">{m.name}</span>
                <span className="font-mono text-gray-600">{done}/{total}</span>
              </div>
            );
          })}
        </div>
      )}
      <div className="text-[12px] text-gray-500 mt-2">What do you want to work on?</div>
    </div>
  );
}

// --- Main Chat ---

export function Chat() {
  const {
    messages,
    setMessages,
    streaming,
    streamText,
    streamTools,
    streamParts,
    thinking,
    usage,
    error,
    loaded,
    historyHydrated,
    connectionStatus,
    lastFailedMessage,
    activeProject,
    send,
    abort,
    retry,
    clear,
    deleteMessage,
    restoreMessage,
  } = useChat();

  const [, setActiveProject] = useActiveProject();
  const [input, setInput] = useState('');
  // perf: render only the tail of huge histories (general.json hit 38MB / thousands of msgs)
  const [chatWindowSize, setChatWindowSize] = useState(80);
  const [cmdIndex, setCmdIndex] = useState(0);
  const [projectBooting, setProjectBooting] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [sessionList, setSessionList] = useState<any[]>([]);
  // Undo buffer for a just-deleted message. Kept ~4s so a misclick is
  // recoverable; after that the delete is permanent (already scrubbed on disk).
  const [pendingDelete, setPendingDelete] = useState<{ msg: ChatMessage; idx: number } | null>(null);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleDeleteMessage = useCallback(async (id: string) => {
    const removed = await deleteMessage(id);
    if (!removed) return;
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setPendingDelete(removed);
    undoTimerRef.current = setTimeout(() => setPendingDelete(null), 4500);
  }, [deleteMessage]);

  const handleUndoDelete = useCallback(() => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setPendingDelete(prev => {
      if (prev) restoreMessage(prev.msg, prev.idx);
      return null;
    });
  }, [restoreMessage]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const contextSentRef = useRef<string | null>(null);
  const switchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Memoized — recomputing this on every render returned a new array ref each
  // time, which cascaded into handleKeyDown's useCallback deps making it new
  // every render and propagated instability up to listeners. Stable refs only
  // when `input` actually changes.
  const filteredCommands = useMemo(() => getFilteredCommands(input), [input]);
  const showCommands = input.startsWith('/') && filteredCommands.length > 0 && !streaming;

  // Auto-scroll: only stop scrolling if the USER actively scrolls up (wheel/touch).
  // Content growing (tool blocks appearing) should NOT disable auto-scroll.
  const userScrolledUpRef = useRef(false);
  const userInteractedRef = useRef(false);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Track user-initiated scroll (wheel or touch)
    const onUserScroll = () => { userInteractedRef.current = true; };
    const onScrollEnd = () => {
      if (userInteractedRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = container;
        const distFromBottom = scrollHeight - scrollTop - clientHeight;
        // User scrolled up intentionally — disable auto-scroll
        // User scrolled back to bottom — re-enable
        userScrolledUpRef.current = distFromBottom > 100;
      }
      userInteractedRef.current = false;
    };

    container.addEventListener('wheel', onUserScroll, { passive: true });
    container.addEventListener('touchmove', onUserScroll, { passive: true });
    container.addEventListener('scroll', onScrollEnd);
    return () => {
      container.removeEventListener('wheel', onUserScroll);
      container.removeEventListener('touchmove', onUserScroll);
      container.removeEventListener('scroll', onScrollEnd);
    };
  }, []);

  useEffect(() => {
    if (!userScrolledUpRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, streamText, streamTools, streamParts]);

  // When switching projects: once history is hydrated, jump to bottom instantly.
  // Resets the user-scrolled-up flag so the next message also auto-scrolls.
  useEffect(() => {
    if (!historyHydrated) return;
    userScrolledUpRef.current = false;
    const container = scrollContainerRef.current;
    if (container) container.scrollTop = container.scrollHeight;
    // Belt-and-suspenders: also after next paint, in case messages render async.
    requestAnimationFrame(() => {
      const c = scrollContainerRef.current;
      if (c) c.scrollTop = c.scrollHeight;
    });
  }, [activeProject?.slug, historyHydrated]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Show soma keepalive turns in chat (message + 'ok' response)
  useEffect(() => {
    const keepaliveHandler = (e: CustomEvent) => {
      const { count } = e.detail;
      // Add the keepalive user message
      const userMsg: ChatMessage = {
        id: `keepalive_user_${count}_${Date.now()}`,
        role: 'user',
        content: `[cache keepalive — respond with just 'ok']`,
        timestamp: Date.now(),
        isKeepAlive: true,
      };
      // Add placeholder for agent response
      const agentMsg: ChatMessage = {
        id: `keepalive_agent_${count}_${Date.now()}`,
        role: 'assistant',
        content: '',
        timestamp: Date.now(),
        isKeepAlive: true,
      };
      setMessages(prev => [...prev, userMsg, agentMsg]);
    };

    const responseHandler = (e: CustomEvent) => {
      const { text, count } = e.detail;
      // Append to the last keepalive agent message
      setMessages(prev => {
        const idx = [...prev].reverse().findIndex(m => m.isKeepAlive && m.role === 'assistant' && m.id.includes(`keepalive_agent_${count}`));
        if (idx === -1) return prev;
        const realIdx = prev.length - 1 - idx;
        return prev.map((m, i) => i === realIdx ? { ...m, content: m.content + text } : m);
      });
    };

    window.addEventListener('beast:keepalive' as any, keepaliveHandler as any);
    window.addEventListener('beast:keepalive_response' as any, responseHandler as any);
    return () => {
      window.removeEventListener('beast:keepalive' as any, keepaliveHandler as any);
      window.removeEventListener('beast:keepalive_response' as any, responseHandler as any);
    };
  }, [setMessages]);

  // When project changes, switch gateway CWD so Soma discovers the right .soma/
  // Only triggers if user stays on the project for 3 seconds — rapid navigation is free.
  // We read initial value from sessionStorage so remounts don't look like project changes.
  const prevProjectRef = useRef<string | null>(
    typeof window !== 'undefined' ? sessionStorage.getItem('beast-prev-project') : null
  );
  // Skip the first render when activeProject is null but sessionStorage has a project.
  // This means usePersistedState hasn't hydrated yet — the real value is coming.
  const hydrationGuardRef = useRef(true);
  useEffect(() => {
    const newSlug = activeProject?.slug ?? null;

    // Hydration guard: if activeProject is null but we KNOW a project was active
    // (from sessionStorage), skip this render — the hydrated value is coming next.
    if (hydrationGuardRef.current) {
      hydrationGuardRef.current = false;
      if (newSlug === null && prevProjectRef.current !== null) {
        return; // wait for hydration
      }
    }

    if (newSlug === prevProjectRef.current) return;
    contextSentRef.current = null;
    const isInitialMount = prevProjectRef.current === null;
    prevProjectRef.current = newSlug;
    if (typeof window !== 'undefined') {
      if (newSlug) sessionStorage.setItem('beast-prev-project', newSlug);
      else sessionStorage.removeItem('beast-prev-project');
    }

    // Cancel any pending switch from a previous project
    if (switchDebounceRef.current) {
      clearTimeout(switchDebounceRef.current);
      switchDebounceRef.current = null;
      setProjectBooting(false);
    }

    if (!activeProject?.path) {
      if (!isInitialMount) {
        gateway.projectSwitch({ path: '.', name: 'Beast', slug: 'beast' }).catch(() => {});
      }
      return;
    }

    // Only boot if user stays on this project for 3 seconds
    switchDebounceRef.current = setTimeout(() => {
      switchDebounceRef.current = null;
      setProjectBooting(true);
      // Safety timeout — never block sending for more than 10 seconds
      const safetyTimeout = setTimeout(() => setProjectBooting(false), 10_000);
      gateway.projectSwitch({
        path: activeProject.path,
        name: activeProject.name,
        slug: activeProject.slug,
      })
        .then(async (data: any) => {
          if (data.ok) {
            console.log(`[beast] Project booted: ${activeProject.name}`);
            // Wait briefly for session to settle, max 5 seconds
            for (let i = 0; i < 10; i++) {
              await new Promise(r => setTimeout(r, 500));
              try {
                const s = await gateway.session();
                if (!s.isStreaming) break;
              } catch {}
            }
          }
        })
        .catch((err: any) => console.error('[beast] Project switch failed:', err))
        .finally(() => { clearTimeout(safetyTimeout); setProjectBooting(false); });
    }, 3000);
  }, [activeProject?.slug]);

  // Process a file — upload to gateway, insert file path into message (Cursor-style)
  const processFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = (reader.result as string).split(',')[1];
      try {
        const data = await gateway.upload({ data: base64, media_type: file.type, name: file.name });
        if (data.ok && data.path) {
          setInput(prev => prev ? `${prev} ${data.path}` : data.path);
        }
      } catch {
        setInput(prev => prev ? `${prev} [image upload failed]` : '[image upload failed]');
      }
    };
    reader.readAsDataURL(file);
  }, []);

  // Global paste listener — catches Cmd+V regardless of focus
  // Must be native DOM event (not React) for reliable preventDefault
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as ClipboardEvent;
      const items = ce.clipboardData?.items;
      if (!items) return;
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          const file = item.getAsFile();
          if (file) processFile(file);
          return;
        }
      }
    };
    // Use capture phase to intercept before ANY other handler
    window.addEventListener('paste', handler, true);
    return () => window.removeEventListener('paste', handler, true);
  }, [processFile]);

  // ATTACH button (Boss 2026-09-03: every chat must attach + stop — paste and
  // drag existed here but there was NO visible button, and Boss shouldn't have
  // to know secret gestures). Images go the proven processFile vision path;
  // any other file uploads into the active session's project uploads/.
  const fileRef = useRef<HTMLInputElement>(null);
  const onAttach = useCallback(async (files: FileList | null) => {
    if (!files?.length) return;
    const landed: string[] = [];
    for (const f of Array.from(files)) {
      if (f.type.startsWith('image/')) { processFile(f); continue; }
      if (f.size > 50 * 1024 * 1024) continue;
      const fd = new FormData(); fd.append('file', f);
      const slug = activeProject?.slug || 'beast';
      try {
        const r = await fetch(`/api/gw/sessions/${encodeURIComponent(slug)}/upload`, { method: 'POST', body: fd });
        const d = await r.json().catch(() => ({}));
        if (r.ok) landed.push(d.path || f.name);
      } catch { /* upload failed — leave it out of the note */ }
    }
    if (landed.length) setInput(v => `${v}${v ? '\n' : ''}[attached: ${landed.join(', ')}] `);
  }, [processFile, activeProject?.slug]);

  // Handle paste — ported from old BCC GatewayChat.tsx (proven working)
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) processFile(file);
        return;
      }
    }
  }, [processFile]);

  // Handle drag & drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    for (const file of Array.from(e.dataTransfer?.files || [])) {
      if (file.type.startsWith('image/')) processFile(file);
    }
  }, [processFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const autoResize = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  }, []);

  useEffect(() => {
    autoResize();
  }, [input, autoResize]);

  // Build project context for the gateway (not shown to user)
  const getProjectContext = useCallback(async (): Promise<string | undefined> => {
    if (!activeProject) return undefined;

    // Always include a lightweight project header so the agent always knows where it is
    const header = `Active project: ${activeProject.name}${activeProject.goal ? ` — Goal: ${activeProject.goal}` : ''}${activeProject.path ? ` — Path: ${activeProject.path}` : ''}`;

    // Full context (preload) only sent once per project switch
    if (contextSentRef.current === activeProject.slug) return header;

    const parts: string[] = [];
    parts.push(`Project: ${activeProject.name}`);
    if (activeProject.path) parts.push(`Path: ${activeProject.path}`);
    if (activeProject.goal) parts.push(`Goal: ${activeProject.goal}`);

    // Try to load preload
    try {
      const data = await projectsApi.preload(activeProject.slug);
      if (data.ok && data.preload) {
        parts.push(`\n--- Project Preload (last session) ---\n${data.preload}`);
      }
    } catch {}

    contextSentRef.current = activeProject.slug;

    // Update lastActiveAt so this project sorts to top
    projectsApi.touch(activeProject.slug).catch(() => {});

    return parts.join('\n');
  }, [activeProject]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;

    // === UI-only commands (don't hit the backend) ===
    if (text === '/clear') { clear(); setInput(''); return; }
    if (text === '/new') {
      clear();
      setInput('');
      gateway.sessionNew().catch(() => {});
      return;
    }
    if (text === '/usage') {
      window.dispatchEvent(new CustomEvent('beast:fetch-usage'));
      setInput('');
      return;
    }
    if (text === '/help') {
      setInput('');
      const helpMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `## Commands

All commands go through Soma — same as Cursor. Beast is just the UI.

**Soma** (handled by Pi SDK extensions):
| Command | Description |
|---------|-------------|
| \`/exhale\` | Save state — preload, session log, kanban |
| \`/inhale [name]\` | Load preload + resume |
| \`/breathe\` | Save state + auto-rotate to fresh session |
| \`/auto-breathe [on/off]\` | Toggle proactive context rotation |
| \`/status\` | Show keepalive, context, session stats |
| \`/keepalive [on/off]\` | Toggle cache keepalive |
| \`/guard-status\` | Show file protection rules |
| \`/hub [install/share/list]\` | Community hub |
| \`/soul-space [on/off]\` | Toggle soul-space introspection |
| \`/scratch <note>\` | Private notes |
| \`/pin <name>\` | Bump protocol/muscle heat |
| \`/kill <name>\` | Drop heat to 0 |
| \`/rest\` | Disable keepalive and exhale |
| \`/focus <keyword>\` | Prime session for a topic |

**Session** (SDK builtins):
| Command | Description |
|---------|-------------|
| \`/compact\` | Manually compact session context |
| \`/reload\` | Reload extensions, skills, prompts |
| \`/export [html/jsonl]\` | Export session to file |

**UI Only:**
| Command | Description |
|---------|-------------|
| \`/usage\` | Fetch Claude Max plan usage |
| \`/clear\` | Clear chat (keep session) |
| \`/new\` | Clear chat + reset session |
| \`/help\` | Show this help |`,
        timestamp: Date.now(),
      };
      setMessages(prev => [...prev, helpMsg]);
      return;
    }

    // === SDK builtins — session-level operations (not LLM, not extensions) ===
    if (text.startsWith('/') && !text.startsWith('/ ')) {
      const cmdName = text.split(' ')[0];
      const cmdArgs = text.slice(cmdName.length).trim();

      const SDK_BUILTINS: Record<string, () => Promise<void>> = {
        '/compact': async () => {
          const data = await sessionApi.compact();
          setMessages(prev => [...prev, { id: crypto.randomUUID(), role: 'assistant', timestamp: Date.now(), content: data.ok ? `📦 **Compacted** — ${data.before}% → ${data.after}%` : `❌ ${data.error}` }]);
        },
        '/reload': async () => {
          const data = await sessionApi.reload();
          setMessages(prev => [...prev, { id: crypto.randomUUID(), role: 'assistant', timestamp: Date.now(), content: data.ok ? `🔄 **Reloaded** — tools: ${data.tools?.join(', ')}` : `❌ ${data.error}` }]);
        },
        '/export': async () => {
          const format = cmdArgs === 'jsonl' ? 'jsonl' : 'html';
          const data = await sessionApi.exportSession(format);
          setMessages(prev => [...prev, { id: crypto.randomUUID(), role: 'assistant', timestamp: Date.now(), content: data.ok ? `📦 **Exported** ${data.format} → \`${data.path}\`` : `❌ ${data.error}` }]);
        },
      };

      if (SDK_BUILTINS[cmdName]) {
        setInput('');
        try { await SDK_BUILTINS[cmdName](); } catch (e: any) {
          setMessages(prev => [...prev, { id: crypto.randomUUID(), role: 'assistant', timestamp: Date.now(), content: `❌ ${e.message}` }]);
        }
        return;
      }
    }

    // === Everything else → session.prompt() (Soma handles it) ===
    // Slash commands go to Pi SDK extensions. Regular text goes to the LLM.
    // This is exactly how Cursor works.
    if (text.startsWith('/') && !text.startsWith('/ ')) {
      // Slash command — send directly, no project context prefix
      setInput('');
      send(text);
    } else {
      // Regular message — include project context on first message
      const context = await getProjectContext();
      send(text, context);
      setInput('');
    }
  }, [input, send, clear, setMessages, getProjectContext]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (showCommands) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setCmdIndex(i => Math.min(i + 1, filteredCommands.length - 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setCmdIndex(i => Math.max(i - 1, 0)); return; }
      if (e.key === 'Tab') {
        e.preventDefault();
        const cmd = filteredCommands[cmdIndex];
        if (cmd) {
          const hasArgs = cmd.usage.includes('<');
          setInput(hasArgs ? cmd.name + ' ' : cmd.name);
          setCmdIndex(0);
        }
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        // If input exactly matches a command, send it instead of autocompleting
        const exactMatch = filteredCommands.some(c => c.name === input.trim());
        if (exactMatch) {
          e.preventDefault();
          handleSubmit(e);
          return;
        }
        e.preventDefault();
        const cmd = filteredCommands[cmdIndex];
        if (cmd) {
          const hasArgs = cmd.usage.includes('<');
          setInput(hasArgs ? cmd.name + ' ' : cmd.name);
          setCmdIndex(0);
        }
        return;
      }
      if (e.key === 'Escape') { e.preventDefault(); setInput(''); setCmdIndex(0); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); }
  }, [handleSubmit, showCommands, filteredCommands, cmdIndex]);

  useEffect(() => { setCmdIndex(0); }, [input]);

  return (
    <div className="flex flex-col h-screen bg-[rgb(var(--background))]"
      onDragOver={(e) => { e.preventDefault(); }}
      onDragEnter={(e) => { e.preventDefault(); }}
      onDrop={(e) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer?.files || []).filter(f => f.type.startsWith('image/'));
        for (const file of files) processFile(file);
      }}>
      {/* Chat Header — project selector + connection status */}
      <div className="flex-shrink-0 flex items-center gap-3 px-5 py-2 border-b border-[rgb(var(--border))]/60">
        <ProjectSelector activeProject={activeProject} setActiveProject={setActiveProject} />
        <div className="flex items-center gap-1.5 mr-2">
          <span className={cn(
            'h-2 w-2 rounded-full flex-shrink-0',
            connectionStatus === 'connected' ? 'bg-emerald-400' : 'bg-red-400'
          )} />
          <span className={cn(
            'text-[11px]',
            connectionStatus === 'connected' ? 'text-gray-500' : 'text-red-400'
          )}>
            {connectionStatus === 'connected' ? 'Connected' : 'Disconnected'}
          </span>
        </div>
        {/* Scratchpad + Session Resume */}
        <div className="relative ml-auto flex items-center gap-1">
          <ScratchpadPanel />
        </div>
        <div className="relative">
          <button
            onClick={async () => {
              if (showSessions) { setShowSessions(false); return; }
              try {
                const data = await soma.sessions(activeProject?.path);
                setSessionList(data.sessions || []);
                setShowSessions(true);
              } catch { setShowSessions(false); }
            }}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] text-gray-500 hover:text-gray-300 hover:bg-white/5 transition-colors"
            title="Resume previous session"
          >
            <RefreshCw className="w-3 h-3" />
            <span>Sessions</span>
          </button>
          {showSessions && sessionList.length > 0 && (
            <div className="absolute right-0 top-full mt-1 w-80 bg-gray-900 border border-[rgb(var(--border))]/60 rounded-lg shadow-xl z-50 max-h-[50vh] overflow-y-auto">
              <div className="px-3 py-2 border-b border-[rgb(var(--border))]/40">
                <div className="text-[10px] text-gray-500 uppercase tracking-wider">Resume Session</div>
              </div>
              {sessionList.map((s: any) => (
                <button
                  key={s.file}
                  onClick={async () => {
                    setShowSessions(false);
                    // Confirm before overwriting current chat view
                    if (messages.length > 0) {
                      const ok = window.confirm(`Load session from ${s.date} ${s.time}?\n\nThis replaces the current chat view. Your current chat is saved on disk and can be reopened from this menu.`);
                      if (!ok) return;
                    }
                    try {
                      const data: any = await soma.loadSession(s.file, activeProject?.path);
                      if (!data?.ok || !Array.isArray(data.messages)) {
                        throw new Error(data?.error || 'failed to load session');
                      }
                      // Start a fresh server-side agent thread (view-only history;
                      // typing a new message won't carry the loaded LLM context).
                      await gateway.sessionNew().catch(() => {});
                      setMessages(data.messages);
                    } catch (err: any) {
                      setMessages(prev => [...prev, {
                        id: crypto.randomUUID(), role: 'assistant' as const, timestamp: Date.now(),
                        content: `❌ Failed to load session: ${err.message}`,
                      }]);
                    }
                  }}
                  className="w-full flex flex-col gap-0.5 px-3 py-2.5 text-left hover:bg-[rgb(var(--muted))]/50 transition-colors border-b border-[rgb(var(--border))]/20 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-gray-400">{s.date} {s.time}</span>
                    <span className="text-[10px] text-gray-600">{s.msgCount} msgs</span>
                  </div>
                  <span className="text-xs text-gray-300 truncate">{s.summary}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <SelectionReadMenu containerRef={scrollContainerRef} />
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {/* EmptyState only after disk-backed history has fully hydrated AND
            localStorage cache is also empty. Two gates because the hook
            renders synchronously from localStorage on first paint, and the
            disk read finishes shortly after — we don't want to flash empty
            during either window. */}
        {loaded && historyHydrated && messages.length === 0 && !hasSavedChat(activeProject?.slug) && (
          <EmptyState activeProject={activeProject} />
        )}

        {messages.length > chatWindowSize && (
          <div className="text-center py-2">
            <button onClick={() => setChatWindowSize(s => s + 200)}
              className="text-xs text-gray-500 hover:text-cyan-300 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.06]">
              Load earlier · {messages.length - chatWindowSize} older messages
            </button>
          </div>
        )}
        {(messages.length > chatWindowSize ? messages.slice(messages.length - chatWindowSize) : messages).map(msg => {
          // During streaming, skip the placeholder assistant message (StreamingBubble shows live content)
          if (streaming && msg.role === 'assistant' && msg === messages[messages.length - 1]) {
            return null;
          }
          return <MessageBubble key={msg.id} msg={msg} onDelete={handleDeleteMessage} />;
        })}

        {streaming && (
          <StreamingBubble text={streamText} tools={streamTools} parts={streamParts} thinking={thinking} />
        )}

        {error && (
          <div className="flex justify-start">
            <div className="rounded-lg px-3 py-2 bg-red-500/10 border border-red-500/20 text-xs text-red-400 flex items-center gap-2">
              <span>{error}</span>
              {lastFailedMessage && (
                <button
                  onClick={retry}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-500/20 hover:bg-red-500/30 text-red-300 transition-colors"
                >
                  <RefreshCw className="w-3 h-3" />
                  <span>Retry</span>
                </button>
              )}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Undo toast — a deleted message is recoverable for a few seconds */}
      {pendingDelete && (
        <div className="flex justify-center pointer-events-none" style={{ position: 'relative' }}>
          <div
            className="pointer-events-auto"
            style={{
              position: 'absolute', bottom: 8, display: 'flex', alignItems: 'center', gap: 10,
              padding: '7px 10px 7px 14px', borderRadius: 9,
              background: 'rgba(28,32,42,0.96)', border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 6px 20px rgba(0,0,0,0.35)', fontSize: 12, color: '#c4ccd8',
              whiteSpace: 'nowrap', zIndex: 20,
            }}
          >
            <span>Message deleted &amp; forgotten</span>
            <button
              onClick={handleUndoDelete}
              className="hover:brightness-125"
              style={{
                border: 'none', cursor: 'pointer', background: 'transparent',
                color: '#67b8ff', fontWeight: 600, fontSize: 12, padding: '2px 4px',
              }}
            >
              Undo
            </button>
          </div>
        </div>
      )}

      {/* Status bar + Input */}
      <div className="flex-shrink-0 border-t border-[rgb(var(--border))]/60 bg-white/[0.05] px-4 pt-2">
        <div className="mb-2">
          <StatusBar />
        </div>

        <form onSubmit={handleSubmit} onDrop={handleDrop} onDragOver={handleDragOver}>
          <div className="relative flex items-end gap-2">
            <CommandPalette
              input={input}
              visible={showCommands}
              selectedIndex={cmdIndex}
              onSelect={(cmd) => {
                const hasArgs = cmd.includes('<');
                setInput(hasArgs ? cmd.split(' ')[0] + ' ' : cmd);
                setCmdIndex(0);
                inputRef.current?.focus();
              }}
              filteredCommands={filteredCommands}
            />
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={projectBooting ? `Loading ${activeProject?.name}…` : connectionStatus === 'disconnected' ? 'Gateway offline…' : activeProject ? `Message Beast about ${activeProject.name}…` : 'Message Beast…'}
              rows={1}
              className="flex-1 bg-[rgb(var(--muted))]/40 border border-[rgb(var(--border))]/40 rounded-xl px-4 py-2.5 text-sm text-gray-200 placeholder:text-gray-600 focus:outline-none focus:border-cyan-500/40 focus:ring-1 focus:ring-cyan-500/20 resize-none min-h-[40px] max-h-[200px]"
              style={{ overflowY: 'hidden' }}
            />
            <input ref={fileRef} type="file" multiple className="hidden"
                   onChange={(e) => { onAttach(e.target.files); e.target.value = ''; }} />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              title="Attach files — images go to vision, other files land in the project's uploads/"
              className="p-2.5 rounded-xl bg-[rgb(var(--muted))]/40 border border-[rgb(var(--border))]/40 text-gray-500 hover:text-gray-200 hover:bg-white/10 transition-colors flex-shrink-0"
            >
              <Paperclip className="w-4 h-4" />
            </button>
            {(streaming || messages.some(m => m.tools?.some(t => t.status === 'running') || m.parts?.some(p => p.type === 'tool' && p.tool.status === 'running'))) && (
              <button
                type="button"
                onClick={abort}
                title="Stop generation"
                className="p-2.5 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 transition-colors flex-shrink-0"
              >
                <Square className="w-4 h-4" />
              </button>
            )}
            <button
              type="submit"
              disabled={!input.trim()}
              title={streaming ? 'Send follow-up' : 'Send message'}
              className="p-2.5 rounded-xl bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </form>
        <div className="h-4" />
      </div>
    </div>
  );
}
